export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export const monthName = (month: number): string => MONTH_NAMES[month - 1] ?? '';

/**
 * V1 duplicate protection is intentionally simple: trim, lowercase, collapse
 * internal whitespace. No punctuation stripping, no fuzzy/geographic matching —
 * see supabase/migrations/20260810042434_create_surf_passport_entries.sql.
 */
export const normalizeDestinationName = (raw: string): string =>
  raw.trim().toLowerCase().replace(/\s+/g, ' ');

export interface PassportEntryCountry {
  id: string;
  name: string;
  iso2: string | null;
}

/** Only the field the dashboard's Last Trip card needs -- real photography,
 * only present when the entry has been linked to a canonical destination
 * (linked_destination_id is nullable and unset by any current UI path). */
export interface PassportEntryLinkedDestination {
  hero_image_url: string | null;
}

export interface PassportEntryRow {
  id: string;
  user_id: string;
  country_id: string;
  destination_name: string;
  destination_name_normalized: string;
  month: number;
  linked_destination_id: string | null;
  created_at: string;
  updated_at: string;
  countries: PassportEntryCountry | null;
  destinations: PassportEntryLinkedDestination | null;
}

export interface PassportCountryGroup {
  countryId: string;
  countryName: string;
  countryIso2: string | null;
  entries: PassportEntryRow[];
  mostRecentCreatedAt: string;
}

/**
 * Groups entries by country. Countries are ordered by their most recently added
 * entry (most recent first); entries within a country are ordered by month
 * ascending. Entries whose country relationship failed to load are skipped
 * rather than shown ungrouped.
 */
export const groupEntriesByCountry = (entries: PassportEntryRow[]): PassportCountryGroup[] => {
  const groups = new Map<string, PassportCountryGroup>();

  for (const entry of entries) {
    if (!entry.countries) continue;
    const existing = groups.get(entry.country_id);
    if (existing) {
      existing.entries.push(entry);
      if (entry.created_at > existing.mostRecentCreatedAt) {
        existing.mostRecentCreatedAt = entry.created_at;
      }
    } else {
      groups.set(entry.country_id, {
        countryId: entry.country_id,
        countryName: entry.countries.name,
        countryIso2: entry.countries.iso2,
        entries: [entry],
        mostRecentCreatedAt: entry.created_at,
      });
    }
  }

  const result = Array.from(groups.values());
  for (const group of result) {
    group.entries.sort((a, b) => a.month - b.month);
  }
  result.sort((a, b) => (a.mostRecentCreatedAt < b.mostRecentCreatedAt ? 1 : -1));
  return result;
};

/** Unique countries surfed — the Passport's headline number, derived from entries. */
export const getUniqueCountryCount = (entries: PassportEntryRow[]): number =>
  new Set(entries.map((e) => e.country_id)).size;

/** ISO2 codes of surfed countries, for the map's highlight state. */
export const getHighlightedIso2Codes = (entries: PassportEntryRow[]): Set<string> => {
  const codes = new Set<string>();
  for (const entry of entries) {
    if (entry.countries?.iso2) codes.add(entry.countries.iso2);
  }
  return codes;
};

/** Destination count per ISO2 code, for the map's hover/tap label. */
export const getDestinationCountsByIso2 = (entries: PassportEntryRow[]): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    const iso2 = entry.countries?.iso2;
    if (!iso2) continue;
    counts.set(iso2, (counts.get(iso2) ?? 0) + 1);
  }
  return counts;
};

export interface RecentCountryRecord {
  countryId: string;
  countryName: string;
  /** For a future map-highlight-on-hover/click affordance -- not wired up yet. */
  countryIso2: string | null;
  destinationName: string;
  month: number;
}

/**
 * A concise, country-led preview: the single most recently added entry per
 * country, most-recently-added country first, capped at `limit`. Assumes
 * `entries` is already ordered most-recent-first (as usePassportEntries
 * returns it) — this is a display-only derivation, not the full history
 * (see groupEntriesByCountry for that).
 */
export const getRecentCountryRecords = (entries: PassportEntryRow[], limit: number): RecentCountryRecord[] => {
  const seenCountryIds = new Set<string>();
  const result: RecentCountryRecord[] = [];

  for (const entry of entries) {
    if (!entry.countries) continue;
    if (seenCountryIds.has(entry.country_id)) continue;
    seenCountryIds.add(entry.country_id);
    result.push({
      countryId: entry.country_id,
      countryName: entry.countries.name,
      countryIso2: entry.countries.iso2,
      destinationName: entry.destination_name,
      month: entry.month,
    });
    if (result.length === limit) break;
  }

  return result;
};
